import type { Cart } from 'types/cart/Cart';
import type { CartLineItem } from 'types/cart/CartLineItem';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import type { Subscription } from 'types/product/Subscription';
import type { Variant } from 'types/product/Variant';

const hasDiscount = ({ cost }: CartLineItem) =>
  cost.subtotalAmount.amount > cost.totalAmount.amount;

export const updateProductPrices = ({
  cart,
  shopifyProduct,
}: {
  cart?: Cart;
  shopifyProduct?: ShopifyProduct;
}): ShopifyProduct | null => {
  if (!cart || !shopifyProduct) {
    return null;
  }

  let variants: Variant[] = [];
  let subscriptions: Subscription[] = [];

  if (cart && shopifyProduct) {
    if (shopifyProduct.variants.length) {
      variants = shopifyProduct.variants.map((item) => {
        const lineItem = cart.lines.find(
          (line) => line.merchandise.id === item.id,
        );
        if (lineItem) {
          return {
            ...item,
            price: lineItem.cost.totalAmount,
            /* A discount is applied to the price, not the compareAtPrice,
             * so if a price reduction has already been applied, eg Softgels
             * and Gummies price drop, the original pre-discount price becomes
             * the compareAtPrice. */
            compareAtPrice: hasDiscount(lineItem)
              ? lineItem.cost.subtotalAmount
              : item.compareAtPrice || lineItem.cost.subtotalAmount,
          };
        }
        return item;
      });
    }

    if (
      'subscriptions' in shopifyProduct &&
      shopifyProduct.subscriptions.length
    ) {
      subscriptions = shopifyProduct.subscriptions.map((item) => {
        const lineItem = cart.lines.find(
          (line) =>
            line.merchandise.id === item.id &&
            line.sellingPlanAllocation?.sellingPlan.id === item.sellingPlan.id,
        );
        if (lineItem) {
          return {
            ...item,
            price: lineItem.cost.totalAmount,
            compareAtPrice: hasDiscount(lineItem)
              ? lineItem.cost.subtotalAmount
              : item.compareAtPrice || lineItem.cost.subtotalAmount,
          };
        }
        return item;
      });
    }
  }

  return {
    ...shopifyProduct,
    variants: variants.sort((a, b) => a.price.amount - b.price.amount),
    subscriptions: subscriptions.sort(
      (a, b) => a.price.amount - b.price.amount,
    ),
  };
};
